import { parseCommandByte } from './ProtocolHeader';

/**
 * 4.0 RT 实时监测（0xF1）与 RT 档位监测（0xF2）。
 *
 * 两者启停都只改 data（完整包 byte4）：
 *   发送 byte4 = 1 → 设备开始持续推送最新采样
 *   发送 byte4 = 0 → 结束推送
 *
 * 推送包 data 布局相同，从 byte4 起：
 *   启停值 + 左键 RT 挡位 + 左键 ADC[2] + 右键 RT 挡位 + 右键 ADC[2]
 * byte4 仍是启停值（1 监测中 / 0 已停止），左键档位从 byte5 开始。
 * ADC[2] 是小端 uint16：低字节在前，高字节在后。
 *
 * 0xF1 给 ADC 行程曲线用；0xF2 给档位设置界面的按下动画用，数据一致，
 * 只是 byte3 类型不同，方便固件区分两个入口。
 *
 * command=4 / byte3=0xF1、0xF2：与 type1 传感器、type3/4 系统设置、0xF0 校准、0xF3 配置错开。
 */
export const RT_MONITOR_COMMAND = 4;
export const RT_MONITOR_TYPE = 0xf1;
export const RT_GEAR_TYPE = 0xf2;
export const RT_MONITOR_START = 1;
export const RT_MONITOR_STOP = 0;

export const RT_MONITOR_OFFSETS = Object.freeze({
  action: 4,
  leftRt: 5,
  leftAdc: 6,
  rightRt: 8,
  rightAdc: 9,
});

/** 推送包至少要覆盖到右键 ADC 高字节 */
export const RT_MONITOR_MIN_LENGTH = 11;

export const RT_ADC_MAX = 0xffff;
export const RT_GEAR_RANGE = Object.freeze({ min: 0, max: 10 });

const readUint16LE = (data, offset) =>
  (Number(data[offset]) | (Number(data[offset + 1]) << 8)) & 0xffff;

export const isRtMonitorControl = (value) =>
  Number(value) === RT_MONITOR_START || Number(value) === RT_MONITOR_STOP;

/**
 * 组装启停包。只写 byte3（0xF1 或 0xF2）和 byte4。
 * @param {number} type - RT_MONITOR_TYPE / RT_GEAR_TYPE
 * @param {number} action - 1 开始 / 0 结束
 * @returns {Uint8Array}
 */
const encodeControlPacket = (type, action) => {
  if (!isRtMonitorControl(action)) {
    throw new Error('RT 监测控制只能是 1（开始）或 0（结束）');
  }
  const data = new Uint8Array(64);
  data[0] = RT_MONITOR_COMMAND;
  data[1] = 0x00;
  data[2] = 0x80 | 1;
  data[3] = type;
  data[4] = Number(action);
  return data;
};

export const encodeRtMonitorControl = (action) =>
  encodeControlPacket(RT_MONITOR_TYPE, action);

export const encodeRtGearControl = (action) =>
  encodeControlPacket(RT_GEAR_TYPE, action);

/**
 * 解析一包推送。启停回显（payload 只有 1 字节）不当作采样。
 * @param {number} type - RT_MONITOR_TYPE / RT_GEAR_TYPE
 * @param {Uint8Array|number[]|null} data
 * @returns {{leftRt:number,leftAdc:number,rightRt:number,rightAdc:number}|null}
 */
const parseSamplePacket = (type, data) => {
  if (!data || data.length < RT_MONITOR_MIN_LENGTH) return null;
  if (parseCommandByte(data[0]).command !== RT_MONITOR_COMMAND) return null;
  if (data[3] !== type) return null;
  if (!isRtMonitorControl(data[RT_MONITOR_OFFSETS.action])) return null;
  // 我们发出的启停包 length=1，设备若原样回显，不要画进曲线
  if ((Number(data[2]) & 0x7f) === 1) return null;

  return {
    leftRt: Number(data[RT_MONITOR_OFFSETS.leftRt]) || 0,
    leftAdc: readUint16LE(data, RT_MONITOR_OFFSETS.leftAdc),
    rightRt: Number(data[RT_MONITOR_OFFSETS.rightRt]) || 0,
    rightAdc: readUint16LE(data, RT_MONITOR_OFFSETS.rightAdc),
  };
};

export const parseRtMonitorSample = (data) =>
  parseSamplePacket(RT_MONITOR_TYPE, data);

export const parseRtGearSample = (data) =>
  parseSamplePacket(RT_GEAR_TYPE, data);

export const isRtMonitorSample = (data) => parseRtMonitorSample(data) != null;

/**
 * 是否是 0xF1 相关的任何回包（控制回显或采样推送）。
 * 监测中设备高频推送，日志入口用它跳过打印，避免刷爆控制台。
 */
export const isRtMonitorPacket = (data) =>
  !!data &&
  data.length > 3 &&
  parseCommandByte(data[0]).command === RT_MONITOR_COMMAND &&
  data[3] === RT_MONITOR_TYPE;

export const isRtGearSample = (data) => parseRtGearSample(data) != null;

const clampAdc = (value) =>
  Math.max(0, Math.min(RT_ADC_MAX, Math.round(Number(value) || 0)));

const clampGear = (value, fallback) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.max(RT_GEAR_RANGE.min, Math.min(RT_GEAR_RANGE.max, Math.round(number)));
};

/**
 * 组装一包与设备回包相同布局的 RT 采样，给解析往返测试用。
 * @param {{action?:number,leftRt?:number,leftAdc?:number,rightRt?:number,rightAdc?:number,type?:number}} sample
 */
export function encodeRtMonitorSample(sample = {}) {
  const data = new Uint8Array(64);
  data[0] = RT_MONITOR_COMMAND;
  data[1] = 0x00;
  data[2] = 0x80 | 7;
  data[3] = sample.type === RT_GEAR_TYPE ? RT_GEAR_TYPE : RT_MONITOR_TYPE;
  data[4] = isRtMonitorControl(sample.action)
    ? Number(sample.action)
    : RT_MONITOR_START;
  data[5] = clampGear(sample.leftRt, 2);
  const leftAdc = clampAdc(sample.leftAdc);
  data[6] = leftAdc & 0xff;
  data[7] = (leftAdc >> 8) & 0xff;
  data[8] = clampGear(sample.rightRt, 3);
  const rightAdc = clampAdc(sample.rightAdc);
  data[9] = rightAdc & 0xff;
  data[10] = (rightAdc >> 8) & 0xff;
  return data;
}
